import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router'
import { Bell, BellOff, ChevronDown, ClipboardPlus, FilePlus2, List, LogOut, Maximize, Plus, Search, Settings, ShieldCheck, User, UserPlus, Building2 } from 'lucide-react'
import { useAuth } from '../../contexts/useAuth'
import { notificationsApi } from '../../api'
import { Dropdown, DropdownItem, Tooltip } from '../ui'
import { getRoleLabel } from '../../lib/roles'
import type { NotificationDto } from '../../api/generated/dtos'

interface TopNavbarProps {
  onToggleSidebar: () => void
}

export function TopNavbar({ onToggleSidebar }: TopNavbarProps) {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const [searchQuery, setSearchQuery] = useState('')
  const [unreadCount, setUnreadCount] = useState(0)
  const [recentNotifications, setRecentNotifications] = useState<NotificationDto[]>([])

  // Poll notifiche ogni 30 secondi. Il dropdown "Notifiche" (children del componente
  // Dropdown) viene sempre costruito da React indipendentemente dallo stato aperto/chiuso
  // interno del Dropdown, quindi lo teniamo aggiornato qui invece che al click.
  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const [count, recent] = await Promise.all([notificationsApi.getUnreadCount(), notificationsApi.getAll(5)])
        setUnreadCount(typeof count === 'number' ? count : count.count ?? 0)
        setRecentNotifications(Array.isArray(recent) ? recent : (recent.data ?? []))
      } catch {
        // Ignora errori di polling
      }
    }
    fetchNotifications()
    const interval = setInterval(fetchNotifications, 30000)
    return () => clearInterval(interval)
  }, [])

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`)
    }
  }

  const handleLogout = async () => {
    await logout()
    navigate('/login')
  }

  return (
    <header className="top-navbar" id="topNavbar">
      <div className="top-navbar-left min-w-0">
        <Tooltip content="Toggle Menu" position="bottom">
          <button className="top-navbar-toggle" type="button" onClick={onToggleSidebar} aria-label="Toggle Menu">
            <List size={20} />
          </button>
        </Tooltip>
        <form className="relative w-full min-w-0 max-w-100" onSubmit={handleSearch}>
          <Search size={16} className="pointer-events-none absolute top-1/2 left-3.5 -translate-y-1/2 text-faint" />
          <input
            type="search"
            className="h-11 w-full min-w-0 rounded-lg border border-border bg-surface pr-3 pl-11 text-sm transition-colors focus:border-primary-500 focus:bg-canvas focus:outline-none"
            placeholder="Cerca clienti, immobili, richieste..."
            autoComplete="off"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </form>
      </div>

      <div className="top-navbar-right shrink-0">
        {/* Quick Actions */}
        <Dropdown
          trigger={
            <Tooltip content="Aggiungi" position="bottom">
              <button className="top-navbar-icon" type="button" aria-label="Aggiungi">
                <Plus size={18} />
              </button>
            </Tooltip>
          }
        >
          <div className="px-3 py-1.5 text-xs font-semibold text-muted">Aggiungi Nuovo</div>
          <DropdownItem onClick={() => navigate('/customers/create')}>
            <span className="flex items-center gap-2">
              <UserPlus size={15} /> Cliente
            </span>
          </DropdownItem>
          <DropdownItem onClick={() => navigate('/properties/create')}>
            <span className="flex items-center gap-2">
              <Building2 size={15} /> Immobile
            </span>
          </DropdownItem>
          <DropdownItem onClick={() => navigate('/requests/create')}>
            <span className="flex items-center gap-2">
              <FilePlus2 size={15} /> Richiesta
            </span>
          </DropdownItem>
          <DropdownItem onClick={() => navigate('/assignments/create')}>
            <span className="flex items-center gap-2">
              <ClipboardPlus size={15} /> Incarico
            </span>
          </DropdownItem>
        </Dropdown>

        {/* Fullscreen Toggle */}
        <Tooltip content="Schermo intero" position="bottom">
          <button
            className="top-navbar-icon hidden! lg:flex!"
            type="button"
            aria-label="Schermo intero"
            onClick={() => {
              if (document.fullscreenElement) {
                document.exitFullscreen()
              } else {
                document.documentElement.requestFullscreen()
              }
            }}
          >
            <Maximize size={18} />
          </button>
        </Tooltip>

        {/* Notifications */}
        <Dropdown
          trigger={
            <Tooltip content="Notifiche" position="bottom">
              <button className="top-navbar-icon relative" type="button" aria-label="Notifiche">
                <Bell size={18} />
                {unreadCount > 0 && <span className="top-navbar-badge">{unreadCount}</span>}
              </button>
            </Tooltip>
          }
        >
          <div className="flex items-center justify-between border-b border-border-subtle px-3 py-2">
            <span className="text-sm font-semibold">Notifiche</span>
            <Link to="/notifications" className="text-xs text-link hover:underline">
              Vedi tutte
            </Link>
          </div>
          {recentNotifications.length === 0 ? (
            <div className="px-3 py-6 text-center text-muted">
              <BellOff size={24} className="mx-auto mb-1" />
              <p className="m-0 text-xs">Nessuna notifica</p>
            </div>
          ) : (
            <div className="max-h-80 overflow-y-auto">
              {recentNotifications.map((n) => (
                <Link
                  key={n.notificationId}
                  to={n.link || '/notifications'}
                  onClick={() => {
                    if (n.isRead) return
                    setRecentNotifications((items) => items.map((item) => item.notificationId === n.notificationId ? { ...item, isRead: true } : item))
                    setUnreadCount((count) => Math.max(0, count - 1))
                    void notificationsApi.markAsRead(n.notificationId).catch(() => {
                      setRecentNotifications((items) => items.map((item) => item.notificationId === n.notificationId ? { ...item, isRead: false } : item))
                      setUnreadCount((count) => count + 1)
                    })
                  }}
                  className={`block border-b border-secondary-50 px-3 py-2 text-left text-sm last:border-b-0 hover:bg-surface ${n.isRead ? 'text-muted' : 'font-medium text-heading'}`}
                >
                  <div className="truncate">{n.title}</div>
                  <div className="truncate text-xs font-normal text-muted">{n.message}</div>
                </Link>
              ))}
            </div>
          )}
        </Dropdown>

        {/* User Profile */}
        <Dropdown
          fitContentWidth
          maxWidth={288}
          trigger={
            <button className="top-navbar-user" type="button" aria-label="Menu utente">
              <div className="top-navbar-avatar">
                {user?.avatarUrl
                  ? <img src={user.avatarUrl} alt="" className="h-full w-full rounded-full object-cover" />
                  : <User size={16} />}
              </div>
              <div className="top-navbar-user-info hidden! md:flex!">
                <span className="top-navbar-user-name">{user?.email}</span>
                <span className="top-navbar-user-role">
                  {getRoleLabel(user?.role)}
                </span>
              </div>
              <ChevronDown size={14} className="hidden md:block" />
            </button>
          }
        >
          <div className="max-w-full truncate border-b border-border-subtle px-3 py-2 text-xs font-semibold text-muted" title={user?.email}>{user?.email}</div>
          <DropdownItem onClick={() => navigate('/profile')}>
            <span className="flex items-center gap-2">
              <User size={15} /> Il Mio Profilo
            </span>
          </DropdownItem>
          <DropdownItem onClick={() => navigate('/agency/settings')}>
            <span className="flex items-center gap-2">
              <Settings size={15} /> Impostazioni
            </span>
          </DropdownItem>
          {user?.isSuperAdmin && (
            <>
              <div className="my-1 border-t border-border-subtle" />
              <DropdownItem onClick={() => navigate('/admin')}>
                <span className="flex items-center gap-2 font-semibold text-link">
                  <ShieldCheck size={15} /> Pannello Super Admin
                </span>
              </DropdownItem>
            </>
          )}
          <div className="my-1 border-t border-border-subtle" />
          <DropdownItem onClick={handleLogout}>
            <span className="flex items-center gap-2 text-negative">
              <LogOut size={15} /> Logout
            </span>
          </DropdownItem>
        </Dropdown>
      </div>
    </header>
  )
}
